import { Box, Typography, Chip, Stack } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { baseNewAndDetailClasses, DisplayTable, metrcTagsClasses, roomDetailClasses } from '../../../../utility';
import apiService from '../../../../../api/apiService';
import NoEntityMsg from '../../../../utility/messages/NoEntityMsg';
import { harvestsKeyTitleMap } from '../../../utils/TableTitles';
import { FRTLContext } from '../../../../../context/FRTLContext';
import { getFrtlAliasString } from '../../../../../utility/frtlUtils';


const _HarvestsTab = props => {
  const { currDarkThemeClass = '', handleErrorWithFeedback, values } = props;
  const [harvests, setHarvests] = useState([]);
  const { frtlContextValue, setFrtlContextValue } = useContext(FRTLContext);
  const [newHarvestData, setNewHarvestData] = useState({
    room_tables: [],
    strains: [],
  });
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('All');

  const { cropplanid } = useParams();

  const fetchHarvests = async () => {
    try {
      const response = await apiService.fetchInstance('cropplan-harvests/?cropplan_id=' + cropplanid);
      setHarvests(response?.data || []);
    } catch (error) {
      console.error('Error fetching harvests:', error);
      if (handleErrorWithFeedback) {
        handleErrorWithFeedback('Failed to fetch harvests');
      }
    }
  };

  const fetchInstances = async () => {
    try {
      if (props?.values?.strains?.[0]?.farmstrain) {
        const response = await apiService.fetchInstance(`new/harvests/?farm_id=${values?.farm}&cropplan=${cropplanid}`);
        setNewHarvestData(response?.data || { room_tables: [], strains: [] });
      }
    } catch (error) {
      console.error('Error fetching harvest instances:', error);
    }
  };

  useEffect(() => {
    fetchHarvests();
  }, [cropplanid]);

  useEffect(() => {
    fetchInstances();
  }, [cropplanid, props?.values?.strains]);

  const getRoomTableName = roomTableId => {
    const roomTable = newHarvestData.room_tables?.find(rt => rt.id === roomTableId);
    return roomTable ? roomTable.name || `Room Table ${roomTable.id}` : roomTableId;
  };

  const getStrainName = strainId => {
    const strain = newHarvestData.strains?.find(s => s.id === strainId);
    return strain ? strain.strain_name : strainId;
  };

  const getFinishedStatus = isFinished => {
    return isFinished ? 'Completed' : 'In Progress';
  };

  const handleStatusFilterChange = status => {
    setSelectedStatusFilter(status);
  };

  const harvestsTableTitles = useMemo(() => harvestsKeyTitleMap.map(e => ({ title: e.title })), []);

  const filteredHarvests = useMemo(() => {
    if (selectedStatusFilter === 'All') {
      return harvests;
    }

    return harvests.filter(harvest => {
      const status = getFinishedStatus(harvest.is_finished);
      return status === selectedStatusFilter;
    });
  }, [harvests, selectedStatusFilter]);

  const harvestsTableEntries = useMemo(() => {
    return filteredHarvests.map((harvest, idx) => {
      return harvestsKeyTitleMap.map(keyTitle => {
        if (keyTitle.key === 'serial_number') {
          return idx + 1;
        }

        const value = harvest[keyTitle.key];

        if (keyTitle.key === 'harvest_name') {
          return value || `Harvest #${harvest.metrc_harvest_id}`;
        }

        if (keyTitle.key === 'room_table') {
          return getRoomTableName(value);
        }

        if (keyTitle.key === 'cropplanstrain') {
          return getStrainName(value);
        }

        if (keyTitle.key === 'is_finished') {
          return getFinishedStatus(value);
        }

        if (keyTitle.key === 'harvest_date') {
          return value ? new Date(value).toLocaleDateString() : '';
        }

        if (keyTitle.key === 'frtl'){
          return getFrtlAliasString(value, frtlContextValue);
        }

        if (keyTitle.key === 'metrc_harvest_id' || keyTitle.key === 'patient_license_number') {
          return value ?? '';
        }

        if (keyTitle.key === 'created_on' || keyTitle.key === 'updated_on') {
          return value ? new Date(value).toLocaleDateString() : '';
        }

        return value ?? '';
      });
    });
  }, [filteredHarvests, newHarvestData]);

  const shouldShowEmptyTable = useMemo(() => {
    return !props?.values?.strains || props?.values?.strains.length === 0;
  }, [props?.values?.strains]);

  const harvestsTableProps = {
    currDarkThemeClass,
    tableTitles: harvestsTableTitles,
    tableEntries: shouldShowEmptyTable ? [] : harvestsTableEntries,
    emptyTableMsg: shouldShowEmptyTable ? <NoEntityMsg /> : 'No harvests available',
    toShowAddBtn: false,
  };

  const harvestStatusFilterProps = {
    direction: 'row',
    spacing: 3,
    alignItems: 'center',
    padding: '10px',
  };

  const harvestStatusFilterTypoProps = {
    className: `${metrcTagsClasses?.filterTitleTypo} ${currDarkThemeClass}`,
  };

  const statusChipsProps = status => ({
    key: status,
    label: status,
    variant: selectedStatusFilter === status ? 'filled' : 'outlined',
    onClick: () => handleStatusFilterChange(status),
    clickable: true,
    className: `${roomDetailClasses?.chip} ${currDarkThemeClass}`,
  });

  const harvestStatusChipsList = (
    <Stack direction="row" spacing={1}>
      <Chip
        key="All"
        label="All"
        variant={selectedStatusFilter === 'All' ? 'filled' : 'outlined'}
        onClick={() => handleStatusFilterChange('All')}
        clickable
        className={`${roomDetailClasses?.chip} ${currDarkThemeClass}`}
      />
      <Chip {...statusChipsProps('In Progress')} />
      <Chip {...statusChipsProps('Completed')} />
    </Stack>
  );

  const plantSubTypoProps = {
    className: `${roomDetailClasses?.tabTitleTypo} ${currDarkThemeClass}`,
  };

  const noEntityMsgProps = {
    headContent: 'No harvests have been completed in this crop run.',
    msg: 'Buckets will be available once you complete the Wet Weight routine.',
  }

  return (
    <Box className={`${baseNewAndDetailClasses?.tableContainer} ${currDarkThemeClass}`}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" padding={1} width="100%">
        <Typography {...plantSubTypoProps}>Harvests</Typography>
      </Stack>

      {shouldShowEmptyTable && <NoEntityMsg {...noEntityMsgProps} />}

      {!shouldShowEmptyTable && (
        <>
          {/* <Stack {...harvestStatusFilterProps}>
            <Typography {...harvestStatusFilterTypoProps}>Filter by Status:</Typography>
            {harvestStatusChipsList}
          </Stack> */}

          <DisplayTable {...harvestsTableProps} />
        </>
      )}
    </Box>
  );
};

export default _HarvestsTab;